import React, { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import { getOrganizations, deleteOrganization, getMuseumDetails } from "../../api/index.js";
import DataTableSubHeader from "../../Components/DataTableSubHeader/DataTableSubHeader.jsx";
import ReactModal from "../../Components/ReactModal/ReactModal.jsx";
import FilterModal from "../../Components/FilterModal/FilterModal.jsx";
import {
  EditIcon,
  DeleteIcon,
  AddIcon,
  FilterIcon,
} from "../../assets/icons/index.js";
import ItemsTable from "../../Components/ItemsTable/ItemsTable.jsx";
import AddOrganizationModal from "../../Components/Organizations/AddOrganizationModal/AddOrganizationModal.jsx";
import EditOrganizationModal from "../../Components/Organizations/EditOrganizationModal/EditOrganizationModal.jsx";
import DeleteModal from "../../Components/Common/DeleteModal/DeleteModal.jsx";
import AuthenticatedFileImage from "../../Components/Common/AuthenticatedFileImage/AuthenticatedFileImage";
import { SubHeaderMobileOption } from "../../Components/SubHeaderMobileOption/SubHeaderMobileOption.jsx";
import "./Organizations.scss";
import { useAppContext } from "../../Components/Context/AppContext.jsx";
import userHasPermission from "../../helpers/formTools/userHasPermission/index.js";
import AdminDropdown from "../../Components/AdminDropdown/AdminDropdown.jsx";
import Spinner from "../../Components/ProgressBar/Spinner.js";

const selectedHeaders = [
  { key: "logo", label: "", width: "80px", sortable: false },
  { key: "name", label: "Nome organizzazione", width: "260px", sortable: false },
  { key: "comune", label: "Comune", width: "200px", sortable: false },
  { key: "localId", label: "Catalogo", width: "140px", sortable: false },
  { key: "actions", label: "", width: "120px", sortable: false },
];

const filterValues = [
  { label: "Nome organizzazione", key: "name" },
  { label: "Comune", key: "comune" },
];

function OrganizationListImage({ organization }) {
  const [coverUrl, setCoverUrl] = useState("");

  useEffect(() => {
    if (organization?.logoUrl) {
      setCoverUrl("");
      return;
    }

    // Catalog organizations (localId === false): fetch copertina from Musei Italiani
    if (organization?.localId !== false || !organization?.id) {
      setCoverUrl("");
      return;
    }

    let cancelled = false;

    getMuseumDetails(organization.id)
      .then((details) => {
        if (!cancelled) {
          setCoverUrl(details?.immagine_copertina || "");
        }
      })
      .catch(() => {
        if (!cancelled) setCoverUrl("");
      });

    return () => {
      cancelled = true;
    };
  }, [organization?.id, organization?.logoUrl, organization?.localId]);

  if (organization?.logoUrl) {
    return (
      <AuthenticatedFileImage
        src={organization.logoUrl}
        alt={`Logo ${organization.name}`}
        className="organization-table-logo"
        fallback={<span className="organization-table-logo--empty">—</span>}
      />
    );
  }

  if (coverUrl) {
    return (
      <img
        src={coverUrl}
        alt={`Copertina ${organization.name}`}
        className="organization-table-logo"
      />
    );
  }

  return <span className="organization-table-logo--empty">—</span>;
}

function getOrganizationComune(organization) {
  if (typeof organization?.comune === "string") {
    return organization.comune;
  }
  return organization?.comune?.nome || "";
}

function applyOrganizationFilters(organizations, filters) {
  if (!Array.isArray(organizations)) return [];
  if (!Array.isArray(filters) || filters.length === 0) {
    return organizations;
  }

  return organizations.filter((organization) =>
    filters.every((item) => {
      if (!item?.key || !item?.value?.trim()) return true;
      const query = item.value.trim().toLowerCase();

      if (item.key === "name") {
        return (organization.name || "").toLowerCase().includes(query);
      }

      if (item.key === "comune") {
        return getOrganizationComune(organization).toLowerCase().includes(query);
      }

      return true;
    })
  );
}

const Organizations = () => {
  const [isMenuActive, setMenuActive] = useState(false);
  const [recordFilterModalOpen, setRecordFilterModalOpen] = useState(false);
  const [organizationFilter, setOrganizationFilter] = useState([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState([]);
  const [convertedData, setConvertedData] = useState([]);
  const [selectedOrganizationData, setSelectedOrganizationData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const { userProfile } = useAppContext();
  const organization = JSON.parse(localStorage.getItem("organization"));
  const userCanRead = userHasPermission({userProfile, organization, resource: "ORGANIZATION", permission: "READ"});
  const userCanWrite = userHasPermission({userProfile, organization, resource: "ORGANIZATION", permission: "WRITE"});
  const userCanDelete = userHasPermission({userProfile, organization, resource: "ORGANIZATION", permission: "DELETE"});

  const breadcrumbsData = useMemo(
    () => [
      { id: 1, label: `Amministrazione`, iconUrl: true },
      { id: 2, label: "ORGANIZZAZIONI", iconUrl: "" },
    ],
    []
  );

  const fetchOrganizations = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getOrganizations({ page: 1, pageSize: 1000 });
      const list = Array.isArray(response)
        ? response
        : response?.results ?? response?.content ?? [];
      setFormData(list);
    } catch (error) {
      toast.error("Errore nel recupero dati delle organizzazioni");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleDeleteOrganization = useCallback(async (id) => {
    try {
      await deleteOrganization(id);
      toast.success("Organizzazione eliminata con successo");
      fetchOrganizations();
    } catch (error) {
      toast.error(
        <div>
          Errore nell'eliminazione dell'organizzazione
          <br />
          {error?.data?.detail ? error?.data?.detail : ""}
        </div>
      );
    } finally {
      setIsDeleteModalOpen(false);
    }
  }, [fetchOrganizations]);

  const handleOpenDeleteModal = useCallback((organizationData) => {
    setSelectedOrganizationData(organizationData);
    setIsDeleteModalOpen(true);
  }, []);

  const handleEditOrganization = useCallback((organizationData) => {
    setSelectedOrganizationData(organizationData);
    setIsEditModalOpen(true);
  }, []);

  const handleCallBackFilter = useCallback((data) => {
    const filter = data
      .map((item) => ({
        key: filterValues.find((f) => f.label === item.label)?.key,
        value: item.value?.trim() ?? "",
      }))
      .filter((item) => item.key && item.value);
    setOrganizationFilter(filter);
    setCurrentPage(1);
    return true;
  }, []);

  const handleResetFilter = useCallback(() => {
    setOrganizationFilter([]);
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(newPage);
  }, []);

  const handleRecordsPerPageChange = useCallback((newRecordsPerPage) => {
    setRecordsPerPage(newRecordsPerPage);
    setCurrentPage(1);
  }, []);

  const filteredOrganizations = useMemo(
    () => applyOrganizationFilters(formData, organizationFilter),
    [formData, organizationFilter]
  );

  const paginatedOrganizations = useMemo(() => {
    const start = (currentPage - 1) * recordsPerPage;
    return filteredOrganizations.slice(start, start + recordsPerPage);
  }, [filteredOrganizations, currentPage, recordsPerPage]);

  const totalFilteredRecords = filteredOrganizations.length;

  const subHeaderRightNavOptions = useMemo(
    () => ({
      icons: [
        userCanWrite && {
          key: "add",
          icon: <AddIcon fillColor="var(--archeo-color)" />,
          onClick: () => setModalOpen(true),
          label: "Aggiungi",
        },
        {
          key: "filter",
          icon: <FilterIcon fillColor="var(--archeo-color)" />,
          onClick: () => setRecordFilterModalOpen(true),
          label: "Filtro",
        },
      ].filter(Boolean),
      components: [
        <AdminDropdown defaultSelected="ORGANIZZAZIONI" />,
      ],
    }),
    [userCanWrite]
  );

  useEffect(() => {
    if (!Array.isArray(paginatedOrganizations)) return;
    const mappedData = paginatedOrganizations.map((result) => {
      const comuneValue = getOrganizationComune(result);

      return {
        name: { value: result.name },
        comune: { value: comuneValue },
        logo: {
          value: result.logoUrl || result.id || "",
          component: <OrganizationListImage organization={result} />,
        },
        localId: {
          value: result.localId,
          component: (
            <span
              className={`organization-local-id-badge ${
                result.localId
                  ? "organization-local-id-badge--local"
                  : "organization-local-id-badge--catalog"
              }`}
            >
              {result.localId ? "Locale" : "Musei Italiani"}
            </span>
          ),
        },
        actions: {
          value: [],
          component: (
            <div className="actions-wrapper">
              <div
                className="action-icon"
                onClick={() => handleEditOrganization(result)}
                style={{ cursor: "pointer" }}
              >
                <EditIcon fillColor="var(--archeo-color)" />
              </div>
              {userCanDelete && (
                <div
                  className="action-icon"
                  onClick={() => handleOpenDeleteModal(result)}
                  style={{ cursor: "pointer" }}
                >
                  <DeleteIcon fillColor="var(--archeo-color)" />
                </div>
              )}
            </div>
          ),
        },
      };
    });
    setConvertedData(mappedData);
  }, [
    paginatedOrganizations,
    userCanWrite,
    userCanDelete,
    handleEditOrganization,
    handleOpenDeleteModal,
  ]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  return (
    <div className="container">
      <main className="page-main-content">
        <DataTableSubHeader
          title="ORGANIZAZIONI"
          breadcrumbsData={breadcrumbsData}
          subHeaderRightNavOptions={subHeaderRightNavOptions}
          isMenuActive={isMenuActive}
          setMenuActive={() => setMenuActive(!isMenuActive)}
        />
        {isLoading ? (
          <Spinner />
        ) : (
        <ItemsTable
          data={convertedData}
          headers={selectedHeaders}
          filterValues={filterValues}
          totalRecords={totalFilteredRecords}
          currentPage={currentPage}
          recordsPerPage={recordsPerPage}
          onPageChange={handlePageChange}
          onRecordsPerPageChange={handleRecordsPerPageChange}
          userCanRead={userCanRead}
        />
        )}
        <ReactModal
          isOpen={recordFilterModalOpen}
          onClose={() => setRecordFilterModalOpen(false)}
        >
          <FilterModal
            closeModal={() => setRecordFilterModalOpen(false)}
            filterValues={filterValues}
            callBackFilter={handleCallBackFilter}
            onReset={handleResetFilter}
          />
        </ReactModal>
        {modalOpen && (
          <AddOrganizationModal
            isOpen={modalOpen}
            onClose={() => setModalOpen(false)}
            fetchOrganizations={fetchOrganizations}
          />
        )}
        {isEditModalOpen && (
          <EditOrganizationModal
            show={isEditModalOpen}
            organizationData={selectedOrganizationData}
            fetchOrganizations={fetchOrganizations}
            handleClose={() => setIsEditModalOpen(false)}
          />
        )}
        {isDeleteModalOpen && (
          <DeleteModal
            show={isDeleteModalOpen}
            item={selectedOrganizationData}
            itemType="Organizzazione"
            handleClose={() => setIsDeleteModalOpen(false)}
            handleDelete={() => handleDeleteOrganization(selectedOrganizationData.id)}
          />
        )}
        {isMenuActive && (
          <SubHeaderMobileOption
            title="ORGANIZATIONS"
            icons={subHeaderRightNavOptions.icons}
            components={subHeaderRightNavOptions.components}
            onCrossClick={() => setMenuActive(false)}
          />
        )}
      </main>
    </div>
  );
};

export default Organizations;
